import React, { useEffect, useMemo, useState } from 'react';
import { FaFileExcel, FaSpinner } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import { Pagination, PaginationType } from '../../utils/pagination.utils';
import { ExcelDownload } from '../../utils/excel.utils';
import { SearchableSelect, SelectWithSearch, type SelectOption } from '../../utils/select-with-search';
import DateRangePicker from '../../components/DateRangePicker';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SiteDistanceRow {
  activityName?: string | null;
  projectName?: string | null;
  site?: string | null;
  siteName?: string | null;
  allocationDate?: string | null;
  engineerAssignDate?: string | null;
  engineerAssignTime?: string | null;
  surveyDate?: string | null;
  surveyTime?: string | null;
  surveyEngineerName?: string | null;
  siteLatitude?: number | string | null;
  siteLongitude?: number | string | null;
  enggAttLatitude?: number | string | null;
  enggAttLongitude?: number | string | null;
  enggHomeLatitude?: number | string | null;
  enggHomeLongitude?: number | string | null;
  /** Road map distance from attendance location (km) */
  siteDistFromAttendanceKm?: number | string | null;
  /** Road map distance from home location (km) */
  siteDistFromHomeKm?: number | string | null;
  /** Minimum road map distance (from Google Maps API) */
  finalDistanceMinKm?: number | string | null;
  siteDistrict?: string | null;
  enggHomeDistrict?: string | null;
  enggWithinState?: string | null;
  selfOrCab?: string | null;
  taSlabCategory?: string | null;
  /** Band the measured minimum running distance falls in, graded by the system */
  systemTaSlabCategory?: string | null;
  /** Band the distance from the engineer's home (reporting) location falls in */
  systemTaSlabFromHome?: string | null;
  /** Band the distance from that day's attendance location falls in */
  systemTaSlabFromAttendance?: string | null;
  slabTa?: number | string | null;
  extraAmount?: number | string | null;
  da?: number | string | null;
  stay?: number | string | null;
  cab?: number | string | null;
  otherExpense?: number | string | null;
  total?: number | string | null;
  remarks?: string | null;
  hillsOrPlain?: string | null;
  state?: string | null;
  /** 'inhouse' = OnRole, 'vendor' = OffRole */
  association?: string | null;
  surveyStatus?: string | null;
  workCompletionStatus?: string | null;
  reportSubmitted?: string | null;
  poNumber?: string | null;
  [key: string]: any;
}

/** Live road-distance lookups one request needed: sent to Google vs answered
 *  from saved distances. Returned by the API next to `pagination`. */
interface DistanceLookups {
  google: number;
  cached: number;
}

const readDistanceLookups = (value: any): DistanceLookups | null =>
  value && typeof value === 'object'
    ? { google: Number(value.google) || 0, cached: Number(value.cached) || 0 }
    : null;

interface SiteDistanceFilters {
  search: string;
  site_id: string;
  site_name: string;
  project_id: string;
  department_id: string;
  activity_id: string;
  state_circle_id: string;
  district_id: string;
  site_engineer_userid: string;
  survey_workflow_status_list: string;
  claim_status_list: string;
  billing_status_list: string;
  system_ta_slab_category: string;
  system_ta_slab_from_home: string;
  system_ta_slab_from_attendance: string;
  workflow_to_status: string;
  site_visit_date: string;
  client_allocation_date_from: string;
  client_allocation_date_to: string;
  site_engineer_assign_date_from: string;
  site_engineer_assign_date_to: string;
  site_engineer_work_date_from: string;
  site_engineer_work_date_to: string;
  allocation_date_from: string;
  allocation_date_to: string;
  survey_date_from: string;
  survey_date_to: string;
  workflow_performed_at_from: string;
  workflow_performed_at_to: string;
  sort_by: string;
  sort_order: 'ASC' | 'DESC';
}

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_FILTERS: SiteDistanceFilters = {
  search: '',
  site_id: '',
  site_name: '',
  project_id: '',
  department_id: '',
  activity_id: '',
  state_circle_id: '',
  district_id: '',
  site_engineer_userid: '',
  survey_workflow_status_list: '',
  claim_status_list: '',
  billing_status_list: '',
  system_ta_slab_category: '',
  system_ta_slab_from_home: '',
  system_ta_slab_from_attendance: '',
  workflow_to_status: '',
  site_visit_date: '',
  client_allocation_date_from: '',
  client_allocation_date_to: '',
  site_engineer_assign_date_from: '',
  site_engineer_assign_date_to: '',
  site_engineer_work_date_from: '',
  site_engineer_work_date_to: '',
  allocation_date_from: '',
  allocation_date_to: '',
  survey_date_from: '',
  survey_date_to: '',
  workflow_performed_at_from: '',
  workflow_performed_at_to: '',
  sort_by: 'created_at',
  sort_order: 'DESC',
};

const MAX_DATE_RANGE_DAYS = 32;

const dateRangeExceedsLimit = (from: string, to: string) => {
  if (!from || !to) return false;
  const fromTime = Date.parse(`${from}T00:00:00Z`);
  const toTime = Date.parse(`${to}T00:00:00Z`);
  return Number.isFinite(fromTime) && Number.isFinite(toTime) &&
    Math.floor((toTime - fromTime) / 86400000) + 1 > MAX_DATE_RANGE_DAYS;
};

// The System TA slab filters — not offered (or sent) on the Without KM report.
const SLAB_FILTER_KEYS: Array<keyof SiteDistanceFilters> = [
  'system_ta_slab_category', 'system_ta_slab_from_home', 'system_ta_slab_from_attendance',
];

const NUMERIC_FILTER_KEYS: Array<keyof SiteDistanceFilters> = [
  'project_id', 'department_id', 'activity_id', 'state_circle_id', 'district_id', 'site_engineer_userid',
];

type DropdownOption = { value: string | number; label: string };

const WORKFLOW_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'site_created', label: 'Site Created' },
  { value: 'engineer_assigned', label: 'Engineer Assigned' },
  { value: 'field_work_in_progress', label: 'Field Work In Progress' },
  { value: 'data_uploaded', label: 'Data Uploaded' },
  { value: 'field_work_completed', label: 'Field Work Completed' },
  { value: 'ta_review_pending', label: 'TA Review Pending' },
  { value: 'ta_review_completed', label: 'TA Review Completed' },
  { value: 'ta_rejected', label: 'TA Rejected' },
  { value: 'mis_report_assign_pending', label: 'MIS Report Assign Pending' },
  { value: 'report_maker_assigned', label: 'Report Maker Assigned' },
  { value: 'report_in_progress', label: 'Report In Progress' },
  { value: 'report_completed', label: 'Report Completed' },
  { value: 'report_maker_rejected', label: 'Report Maker Rejected' },
  { value: 'validator_assigned', label: 'Validator Assigned' },
  { value: 'validation_in_progress', label: 'Validation In Progress' },
  { value: 'validation_completed', label: 'Validation Completed' },
  { value: 'validation_rejected', label: 'Validation Rejected' },
  { value: 'report_finalized', label: 'Report Finalized' },
  { value: 'report_sent_to_client', label: 'Report Sent To Client' },
];

const CLAIM_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const BILLING_STATUS_OPTIONS: DropdownOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'wcc_raised', label: 'WCC Raised' },
  { value: 'wcc_approved', label: 'WCC Approved' },
  { value: 'invoice_raised', label: 'Invoice Raised' },
  { value: 'invoice_submitted', label: 'Invoice Submitted' },
  { value: 'paid', label: 'Paid' },
];

/* System TA slab bands — the distance groups the report grades a site into.
 * "Minimum" grades the smaller of the two distances, "Home" the distance from
 * the engineer's home (reporting) location, "Attendance" the distance from where
 * they marked attendance. All three use the same bands, inclusive at the top:
 * exactly 30 km is "0-30", 30.01 km is "30-60". Home / Attendance are
 * informational only. Values must match TA_SLAB_BUCKETS on the server. */
const SYSTEM_TA_SLAB_OPTIONS: DropdownOption[] = [
  { value: '0-30', label: '0-30' },
  { value: '30-60', label: '30-60' },
  { value: '60-90', label: '60-90' },
  { value: '90-120', label: '90-120' },
  { value: '120-150', label: '120-150' },
  { value: '150+', label: 'Above 150' },
];
const SYSTEM_TA_SLAB_SOURCE_OPTIONS = SYSTEM_TA_SLAB_OPTIONS;

const TABLE_COLUMNS: { key: keyof SiteDistanceRow; label: string }[] = [
  { key: 'activityName',             label: 'Activity Name' },
  { key: 'projectName',              label: 'Project Name' },
  { key: 'site',                     label: 'Site ID' },
  { key: 'siteName',                 label: 'Site Name' },
  { key: 'allocationDate',           label: 'Allocation Date' },
  { key: 'engineerAssignDate',       label: 'Assign Date & Time to Engineer' },
  { key: 'surveyDate',               label: 'Survey Date' },
  { key: 'surveyEngineerName',       label: 'Survey Engineer' },
  { key: 'siteLatitude',             label: 'Site Latitude' },
  { key: 'siteLongitude',            label: 'Site Longitude' },
  { key: 'enggAttLatitude',          label: 'Engg Att Latitude' },
  { key: 'enggAttLongitude',         label: 'Engg Att Longitude' },
  { key: 'enggHomeLatitude',         label: 'Engg Home Latitude' },
  { key: 'enggHomeLongitude',        label: 'Engg Home Longitude' },
  { key: 'siteDistFromAttendanceKm', label: 'Road Map Dist From Attendance (km)' },
  { key: 'siteDistFromHomeKm',       label: 'Road Map Dist From Home (km)' },
  { key: 'finalDistanceMinKm',       label: 'Min Road Map Distance (km)' },
  { key: 'siteDistrict',             label: 'Site District' },
  { key: 'enggHomeDistrict',         label: 'Eng Home District' },
  { key: 'enggWithinState',          label: 'Engg Within State/Outside State' },
  { key: 'selfOrCab',                label: 'Self/Cab' },
  { key: 'taSlabCategory',           label: 'TA Slab Category' },
  { key: 'systemTaSlabFromAttendance', label: 'System TA Slab Category Attendance' },
  { key: 'systemTaSlabFromHome',     label: 'System TA Slab Category Home' },
  { key: 'systemTaSlabCategory',     label: 'System TA Slab Category Minimum' },
  { key: 'slabTa',                   label: 'Slab TA' },
  { key: 'extraAmount',              label: 'Extra Amount' },
  { key: 'da',                       label: 'DA' },
  { key: 'stay',                     label: 'Stay' },
  { key: 'cab',                      label: 'Cab' },
  { key: 'otherExpense',             label: 'Other Expense' },
  { key: 'total',                    label: 'Total' },
  { key: 'remarks',                  label: 'Remarks' },
  { key: 'hillsOrPlain',             label: 'Hills/Plain' },
  { key: 'state',                    label: 'State' },
  { key: 'association',              label: 'Association Type' },
  { key: 'surveyStatus',             label: 'Survey Status' },
  { key: 'workCompletionStatus',     label: 'Work Completion Status' },
  { key: 'reportSubmitted',          label: 'Report Submitted' },
  { key: 'poNumber',                 label: 'PO Number' },
];

/* Two reports share this page.
 *  - with_km:    Site Distance Report (With KM) — the full report.
 *  - without_km: Site Distance Report 2 (Without KM) — the same columns, but
 *    the three road distances and the three System TA slab bands read "NA"
 *    (the server withholds them too), and the slab filters are not offered. */
export type SiteDistanceVariant = 'with_km' | 'without_km';

const VARIANT_CONFIG: Record<SiteDistanceVariant, {
  title: string;
  endpoint: string;
  exportFilename: string;
  exportTitle: string;
  /** Columns always shown / exported as "NA" in this variant. */
  hiddenKeys: ReadonlyArray<keyof SiteDistanceRow>;
}> = {
  with_km: {
    title: 'Site Distance Report (With KM)',
    endpoint: '/reports/site-distance',
    exportFilename: 'site_distance_reports',
    exportTitle: 'Export Site Distance Reports (With KM)',
    hiddenKeys: [],
  },
  without_km: {
    title: 'Site Distance Report 2 (Without KM)',
    endpoint: '/reports/site-distance-without-km',
    exportFilename: 'site_distance_reports_without_km',
    exportTitle: 'Export Site Distance Reports 2 (Without KM)',
    hiddenKeys: [
      'siteDistFromAttendanceKm', 'siteDistFromHomeKm', 'finalDistanceMinKm',
      'systemTaSlabFromAttendance', 'systemTaSlabFromHome', 'systemTaSlabCategory',
    ],
  },
};

// Each date column and the row field holding its clock time, if it has one.
// The API sends dates as YYYY-MM-DD and times as "hh:mm AM/PM", both in IST.
const DATE_TIME_FIELDS: Partial<Record<keyof SiteDistanceRow, keyof SiteDistanceRow | null>> = {
  allocationDate: null,
  engineerAssignDate: 'engineerAssignTime',
  surveyDate: 'surveyTime',
};

// A YYYY-MM-DD day read as written — new Date('YYYY-MM-DD') is UTC midnight,
// which is what used to show up as 5:30 AM.
const formatDay = (value: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Maps backend association_type values to display labels */
const ASSOCIATION_LABEL_MAP: Record<string, string> = {
  inhouse: 'OnRole',
  vendor: 'OffRole',
};

const DISTANCE_KEYS = ['siteDistFromAttendanceKm', 'siteDistFromHomeKm', 'finalDistanceMinKm'];

// Distances show as whole numbers with no unit — the column header carries it.
// null when the cell is not a numeric distance.
const roundedDistance = (key: string, value: unknown): number | null => {
  if (!DISTANCE_KEYS.includes(key) || value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? Math.round(num) : null;
};

const formatCellValue = (key: string, value: unknown, row?: SiteDistanceRow): string => {
  if (value === null || value === undefined || value === '') return 'NA';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (key in DATE_TIME_FIELDS && typeof value === 'string') {
    const timeKey = DATE_TIME_FIELDS[key as keyof SiteDistanceRow];
    const time = timeKey ? row?.[timeKey] : null;
    return time ? `${formatDay(value)}, ${time}` : formatDay(value);
  }
  // Translate association type values
  if (key === 'association' && typeof value === 'string') {
    return ASSOCIATION_LABEL_MAP[value.toLowerCase()] ?? value;
  }
  const distance = roundedDistance(key, value);
  if (distance !== null) return String(distance);
  return String(value);
};

const parseMultiValue = (value: string, options: DropdownOption[]): DropdownOption[] => {
  if (!value) return [];
  return value.split(',').map((v) => v.trim()).filter(Boolean).map((v) => {
    const match = options.find((o) => String(o.value) === v);
    return match || { value: v, label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) };
  });
};

// ─── Filter Form ──────────────────────────────────────────────────────────────

interface FilterFormProps {
  title: string;
  /** false on the Without KM report: it has no System TA slab to filter on. */
  showSlabFilters: boolean;
  filters: SiteDistanceFilters;
  onChange: (key: keyof SiteDistanceFilters, value: string) => void;
  onApply: () => void;
  onReset: () => void;
}

const FilterForm: React.FC<FilterFormProps> = ({ title, showSlabFilters, filters, onChange, onApply, onReset }) => {
  const [selectedProject, setSelectedProject] = useState<SelectOption | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<SelectOption | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<SelectOption | null>(null);
  const [selectedEngineer, setSelectedEngineer] = useState<SelectOption | null>(null);
  const [selectedStateCircle, setSelectedStateCircle] = useState<SelectOption | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<SelectOption | null>(null);

  const surveyWorkflowValues = useMemo(() => parseMultiValue(filters.survey_workflow_status_list, WORKFLOW_STATUS_OPTIONS), [filters.survey_workflow_status_list]);
  const claimStatusValues = useMemo(() => parseMultiValue(filters.claim_status_list, CLAIM_STATUS_OPTIONS), [filters.claim_status_list]);
  const billingStatusValues = useMemo(() => parseMultiValue(filters.billing_status_list, BILLING_STATUS_OPTIONS), [filters.billing_status_list]);
  const systemTaSlabValues = useMemo(() => parseMultiValue(filters.system_ta_slab_category, SYSTEM_TA_SLAB_OPTIONS), [filters.system_ta_slab_category]);
  const systemTaSlabFromHomeValues = useMemo(() => parseMultiValue(filters.system_ta_slab_from_home, SYSTEM_TA_SLAB_SOURCE_OPTIONS), [filters.system_ta_slab_from_home]);
  const systemTaSlabFromAttendanceValues = useMemo(() => parseMultiValue(filters.system_ta_slab_from_attendance, SYSTEM_TA_SLAB_SOURCE_OPTIONS), [filters.system_ta_slab_from_attendance]);

  const selectedStateCircleId = selectedStateCircle ? Number(selectedStateCircle.value) : filters.state_circle_id ? Number(filters.state_circle_id) : null;

  useEffect(() => {
    if (!filters.project_id) { setSelectedProject(null); return; }
    setSelectedProject((prev) => {
      if (prev && String(prev.value) === filters.project_id) return prev;
      return { id: Number(filters.project_id), value: Number(filters.project_id), label: `Project #${filters.project_id}` };
    });
  }, [filters.project_id]);

  useEffect(() => {
    if (!filters.department_id) { setSelectedDepartment(null); return; }
    setSelectedDepartment((prev) => {
      if (prev && String(prev.value) === filters.department_id) return prev;
      return { id: Number(filters.department_id), value: Number(filters.department_id), label: `Department #${filters.department_id}` };
    });
  }, [filters.department_id]);

  useEffect(() => {
    if (!filters.activity_id) { setSelectedActivity(null); return; }
    setSelectedActivity((prev) => {
      if (prev && String(prev.value) === filters.activity_id) return prev;
      return { id: Number(filters.activity_id), value: Number(filters.activity_id), label: `Activity #${filters.activity_id}` };
    });
  }, [filters.activity_id]);

  useEffect(() => {
    if (!filters.site_engineer_userid) { setSelectedEngineer(null); return; }
    setSelectedEngineer((prev) => {
      if (prev && String(prev.value) === filters.site_engineer_userid) return prev;
      return { id: Number(filters.site_engineer_userid), value: Number(filters.site_engineer_userid), label: `User #${filters.site_engineer_userid}` };
    });
  }, [filters.site_engineer_userid]);

  useEffect(() => {
    if (!filters.state_circle_id) { setSelectedStateCircle(null); return; }
    setSelectedStateCircle((prev) => {
      if (prev && String(prev.value) === filters.state_circle_id) return prev;
      return { id: Number(filters.state_circle_id), value: Number(filters.state_circle_id), label: `State Circle #${filters.state_circle_id}` };
    });
  }, [filters.state_circle_id]);

  useEffect(() => {
    if (!filters.district_id) { setSelectedDistrict(null); return; }
    setSelectedDistrict((prev) => {
      if (prev && String(prev.value) === filters.district_id) return prev;
      return { id: Number(filters.district_id), value: Number(filters.district_id), label: `District #${filters.district_id}` };
    });
  }, [filters.district_id]);

  const textInput = (key: keyof SiteDistanceFilters, label: string, placeholder = '') => (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      <input
        type="text"
        value={filters[key]}
        onChange={(e) => onChange(key, e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
      />
    </label>
  );

  // One input per range: the calendar writes both the _from and the _to key.
  const dateRangeInput = (
    fromKey: keyof SiteDistanceFilters,
    toKey: keyof SiteDistanceFilters,
    label: string,
  ) => (
    <DateRangePicker
      label={label}
      from={filters[fromKey]}
      to={filters[toKey]}
      maxSpanDays={MAX_DATE_RANGE_DAYS}
      onChange={(from, to) => { onChange(fromKey, from); onChange(toKey, to); }}
    />
  );

  const selectInput = (key: keyof SiteDistanceFilters, label: string, options: DropdownOption[]) => (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      <select
        value={filters[key]}
        onChange={(e) => onChange(key, e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );

  const multiSelect = (key: keyof SiteDistanceFilters, label: string, value: DropdownOption[], options: DropdownOption[]) => (
    <div className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      <SearchableSelect
        value={value}
        onChange={(selected: DropdownOption[]) => onChange(key, (selected || []).map((i) => String(i.value)).join(','))}
        options={options}
        placeholder={`Select ${label.toLowerCase()}`}
        isMulti
        closeOnOutsideClick={false}
      />
    </div>
  );

  const applyFilters = () => {
    const ranges: Array<[string, string, string]> = [
      [filters.client_allocation_date_from, filters.client_allocation_date_to, 'Client Allocation Date'],
      [filters.site_engineer_assign_date_from, filters.site_engineer_assign_date_to, 'Site Engineer Assign Date'],
      [filters.site_engineer_work_date_from, filters.site_engineer_work_date_to, 'Site Engineer Work Date'],
      [filters.allocation_date_from, filters.allocation_date_to, 'Allocation Date'],
      [filters.survey_date_from, filters.survey_date_to, 'Survey Date'],
      [filters.workflow_performed_at_from, filters.workflow_performed_at_to, 'Workflow Performed At'],
    ];
    const overLimit = ranges.find(([from, to]) => dateRangeExceedsLimit(from, to));
    if (overLimit) {
      toast.error(`${overLimit[2]} cannot span more than ${MAX_DATE_RANGE_DAYS} days.`);
      return;
    }
    onApply();
  };

  return (
    <div className="bg-white shadow-sm rounded border border-gray-200">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title} Filters</h2>
          <p className="text-sm text-gray-600">Apply filters before loading report data.</p>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Search & Filters */}
        <section>
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Search and Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {textInput('search', 'Search', 'airtel')}
            {textInput('site_id', 'Site', 'SIT')}
            {textInput('site_name', 'Site Name', 'Tower')}
            <div className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Project</span>
              <SelectWithSearch
                placeholder="Search project"
                value={selectedProject}
                onChange={(o) => { setSelectedProject(o); onChange('project_id', o ? String(o.value) : ''); }}
                // Scoped on the server to the signed-in user's own projects
                // (super admin: all), the same rule the report rows follow.
                apiEndpoint="/reports/site/projects"
                dataKey="projects"
                labelKey="project_name"
                valueKey="id"
                searchParam="search"
                className="w-full"
              />
            </div>
            <div className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Department / Project Group</span>
              <SelectWithSearch
                placeholder="Search department"
                value={selectedDepartment}
                onChange={(o) => { setSelectedDepartment(o); onChange('department_id', o ? String(o.value) : ''); }}
                apiEndpoint="/departments/list"
                dataKey="departments"
                labelKey="name"
                valueKey="id"
                searchParam="search"
                extraParams={{ assigned_only: true }}
                className="w-full"
              />
            </div>
            <div className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Activity</span>
              <SelectWithSearch
                placeholder="Search activity"
                value={selectedActivity}
                onChange={(o) => { setSelectedActivity(o); onChange('activity_id', o ? String(o.value) : ''); }}
                apiEndpoint="/activities/list"
                dataKey="activities"
                labelKey="activity_name"
                valueKey="id"
                searchParam="search"
                className="w-full"
              />
            </div>
            <div className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">State Circle</span>
              <SelectWithSearch
                placeholder="Search state circle"
                value={selectedStateCircle}
                onChange={(o) => { setSelectedStateCircle(o); setSelectedDistrict(null); onChange('state_circle_id', o ? String(o.value) : ''); onChange('district_id', ''); }}
                apiEndpoint="/state-circle/list"
                dataKey="states"
                labelKey="state_name"
                valueKey="id"
                searchParam="search"
                className="w-full"
              />
            </div>
            <div className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">District</span>
              <SelectWithSearch
                placeholder="Search district"
                value={selectedDistrict}
                onChange={(o) => { setSelectedDistrict(o); onChange('district_id', o ? String(o.value) : ''); }}
                apiEndpoint="/state-circle"
                dataKey="districts"
                labelKey="name"
                valueKey="id"
                searchParam="search"
                dependencyId={selectedStateCircleId}
                className="w-full"
              />
            </div>
            <div className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Site Engineer</span>
              <SelectWithSearch
                placeholder="Search engineer"
                value={selectedEngineer}
                onChange={(o) => { setSelectedEngineer(o); onChange('site_engineer_userid', o ? String(o.value) : ''); }}
                apiEndpoint="/users/list"
                dataKey="users"
                labelKey="first_name"
                valueKey="id"
                searchParam="search"
                displayFormat={(item: any) => {
                  const name = [item.first_name, item.last_name].filter(Boolean).join(' ').trim();
                  return `${name || `User #${item.id}`}${item.employee_code ? ` (${item.employee_code})` : ''}`;
                }}
                className="w-full"
              />
            </div>
            {multiSelect('survey_workflow_status_list', 'Survey Workflow Status List', surveyWorkflowValues, WORKFLOW_STATUS_OPTIONS)}
            {multiSelect('claim_status_list', 'Claim Status List', claimStatusValues, CLAIM_STATUS_OPTIONS)}
            {multiSelect('billing_status_list', 'Billing Status List', billingStatusValues, BILLING_STATUS_OPTIONS)}
            {showSlabFilters && (
              <>
                {multiSelect('system_ta_slab_from_attendance', 'System TA Slab Category Attendance', systemTaSlabFromAttendanceValues, SYSTEM_TA_SLAB_SOURCE_OPTIONS)}
                {multiSelect('system_ta_slab_from_home', 'System TA Slab Category Home', systemTaSlabFromHomeValues, SYSTEM_TA_SLAB_SOURCE_OPTIONS)}
                {multiSelect('system_ta_slab_category', 'System TA Slab Category Minimum', systemTaSlabValues, SYSTEM_TA_SLAB_OPTIONS)}
              </>
            )}
            {selectInput('workflow_to_status', 'Workflow To Status', [{ value: '', label: 'All' }, ...WORKFLOW_STATUS_OPTIONS])}
          </div>
        </section>

        {/* Site Visit Date */}
        <section>
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Site Visit Date</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <DateRangePicker
              single
              label="Site Visit Date"
              from={filters.site_visit_date}
              to=""
              onChange={(date) => onChange('site_visit_date', date)}
            />
          </div>
        </section>

        {/* Date Ranges */}
        <section>
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Date Ranges</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {dateRangeInput('client_allocation_date_from', 'client_allocation_date_to', 'Client Allocation Date')}
            {dateRangeInput('site_engineer_assign_date_from', 'site_engineer_assign_date_to', 'Site Engineer Assign Date')}
            {dateRangeInput('site_engineer_work_date_from', 'site_engineer_work_date_to', 'Site Engineer Work Date')}
            {dateRangeInput('allocation_date_from', 'allocation_date_to', 'Allocation Date')}
            {dateRangeInput('survey_date_from', 'survey_date_to', 'Survey Date')}
            {dateRangeInput('workflow_performed_at_from', 'workflow_performed_at_to', 'Workflow Performed At')}
          </div>
        </section>

        {/* Sorting */}
        <section>
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Sorting</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {selectInput('sort_by', 'Sort By', [
              { value: 'created_at', label: 'created_at' },
              { value: 'allocation_date', label: 'allocation_date' },
              { value: 'survey_date', label: 'survey_date' },
              { value: 'site_name', label: 'site_name' },
              { value: 'site_id', label: 'site_id' },
              { value: 'client_allocation_date', label: 'client_allocation_date' },
              { value: 'site_engineer_assign_date', label: 'site_engineer_assign_date' },
              { value: 'site_engineer_work_date', label: 'site_engineer_work_date' },
            ])}
            {selectInput('sort_order', 'Sort Order', [
              { value: 'DESC', label: 'DESC' },
              { value: 'ASC', label: 'ASC' },
            ])}
          </div>
        </section>
      </div>

      <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
        <button onClick={onReset} className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
          Reset
        </button>
        <button onClick={applyFilters} className="px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900">
          Apply Filters
        </button>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

interface SiteDistanceReportProps {
  variant?: SiteDistanceVariant;
}

const SiteDistanceReport: React.FC<SiteDistanceReportProps> = ({ variant = 'with_km' }) => {
  const config = VARIANT_CONFIG[variant];
  const withoutKm = variant === 'without_km';
  // A withheld column reads "NA" whatever the row carries.
  const cellValue = (key: keyof SiteDistanceRow, row: SiteDistanceRow): string =>
    config.hiddenKeys.includes(key) ? 'NA' : formatCellValue(key as string, row[key], row);
  const [rows, setRows] = useState<SiteDistanceRow[]>([]);
  const [pagination, setPagination] = useState<NormalizedPagination>({
    total_items: 0, current_page: 1, total_pages: 1,
    items_per_page: 50, has_next_page: false, has_previous_page: false,
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [draftFilters, setDraftFilters] = useState<SiteDistanceFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<SiteDistanceFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilterForm, setShowFilterForm] = useState(true);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [exportData, setExportData] = useState<Record<string, string | number>[]>([]);
  const [exportTotal, setExportTotal] = useState(0);
  const [distanceLookups, setDistanceLookups] = useState<DistanceLookups | null>(null);
  // Rows each fetched page returned, for the current filters and page size.
  // Every new fetch adds to it; re-fetching a page replaces that page's count
  // rather than counting its sites twice.
  const [loadedPages, setLoadedPages] = useState<Record<number, number>>({});
  const sitesLoadedSoFar = useMemo(
    () => Object.values(loadedPages).reduce((sum, count) => sum + count, 0),
    [loadedPages]
  );

  const activeFiltersCount = useMemo(() => {
    return Object.entries(appliedFilters).filter(([, v]) => Boolean(v)).length;
  }, [appliedFilters]);

  const buildParams = (targetPage: number, targetLimit: number, filters: SiteDistanceFilters) => {
    const params: Record<string, string | number | boolean> = { page: targetPage, limit: targetLimit };
    (Object.keys(filters) as Array<keyof SiteDistanceFilters>).forEach((key) => {
      const value = filters[key];
      if (!value) return;
      if (withoutKm && SLAB_FILTER_KEYS.includes(key)) return;
      if (NUMERIC_FILTER_KEYS.includes(key)) {
        const n = Number(value);
        if (!Number.isNaN(n)) params[key] = n;
        return;
      }
      params[key] = value;
    });
    return params;
  };

  const fetchRows = async (
    targetPage: number = page,
    targetLimit: number = pagination.items_per_page,
    filters: SiteDistanceFilters = appliedFilters
  ) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(config.endpoint, {
        params: buildParams(targetPage, targetLimit, filters),
      });
      if (response.data.success) {
        const payload = response.data.data || {};
        setRows(payload.data ?? []);
        setDistanceLookups(readDistanceLookups(payload.distance_lookups));
        const p = payload.pagination || {};
        const total = Number(p.total ?? p.total_items ?? p.total_count) || (payload.data?.length ?? 0);
        const currentPage = Number(p.current_page ?? p.page) || targetPage;
        const perPage = Number(p.per_page ?? p.limit ?? p.items_per_page) || targetLimit;
        const pages = Number(p.total_pages) || Math.max(1, Math.ceil(total / perPage));
        setLoadedPages((prev) => ({ ...prev, [currentPage]: (payload.data ?? []).length }));
        setPagination({
          total_items: total, current_page: currentPage, total_pages: pages,
          items_per_page: perPage,
          has_next_page: Boolean(p.has_next_page ?? currentPage < pages),
          has_previous_page: Boolean(p.has_prev_page ?? currentPage > 1),
        });
      }
    } catch {
      toast.error('Failed to fetch site distance report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasSearched) return;
    fetchRows(page, pagination.items_per_page, appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, hasSearched]);

  const handleFilterChange = (key: keyof SiteDistanceFilters, value: string) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(draftFilters);
    setHasSearched(true);
    setShowFilterForm(false);
    setPage(1);
    setLoadedPages({});
    fetchRows(1, pagination.items_per_page, draftFilters);
  };

  const handleResetDraft = () => {
    setDraftFilters(DEFAULT_FILTERS);
  };

  const handleClearAll = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setHasSearched(false);
    setShowFilterForm(true);
    setRows([]);
    setDistanceLookups(null);
    setLoadedPages({});
    setPage(1);
    setPagination({ total_items: 0, current_page: 1, total_pages: 1, items_per_page: 50, has_next_page: false, has_previous_page: false });
  };

  const buildExportRows = (data: SiteDistanceRow[]) =>
    data.map((row) =>
      Object.fromEntries(
        TABLE_COLUMNS.map(({ key, label }) => [
          label,
          config.hiddenKeys.includes(key)
            ? 'NA'
            : roundedDistance(key as string, row[key]) ?? formatCellValue(key as string, row[key], row),
        ])
      )
    );

  const fetchExportData = async () => {
    try {
      setExporting(true);
      let cur = 1;
      let totalPages = 1;
      let total = 0;
      const all: SiteDistanceRow[] = [];
      do {
        const res = await axiosInstance.get(config.endpoint, { params: buildParams(cur, 100, appliedFilters) });
        if (!res.data.success) break;
        const payload = res.data.data || {};
        all.push(...(payload.data ?? []));
        const p = payload.pagination || {};
        if (cur === 1) total = Number(p.total ?? p.total_count) || 0;
        totalPages = Number(p.total_pages) || Math.max(1, Math.ceil((Number(p.total ?? p.total_count) || all.length) / 100));
        cur += 1;
      } while (cur <= totalPages);
      setExportTotal(total || all.length);
      setExportData(buildExportRows(all));
      setShowExcelModal(true);
    } catch {
      toast.error('Failed to prepare export');
    } finally {
      setExporting(false);
    }
  };

  const paginationAdapter: PaginationType = {
    offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
    limit: pagination.items_per_page,
    page: pagination.current_page,
    total_pages: pagination.total_pages,
    total_count: pagination.total_items,
    has_next_page: pagination.has_next_page,
    has_prev_page: pagination.has_previous_page,
    next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
    prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null,
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{config.title}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {withoutKm
                ? 'Apply filters to view site engineer data. Road distances and System TA slabs are not shown in this report. Null values shown as NA.'
                : 'Apply filters to view site engineer distance data. Null values shown as NA.'}
            </p>
            {hasSearched && !showFilterForm && !loading && (
              <div className="mt-2">
                <p className="text-sm font-medium text-gray-800">
                  Sites fetched so far: {sitesLoadedSoFar.toLocaleString('en-IN')}
                  <span className="font-normal text-gray-500">
                    {' '}of {pagination.total_items.toLocaleString('en-IN')} total
                    {' '}· this fetch: {rows.length.toLocaleString('en-IN')}
                    {' '}· {Object.keys(loadedPages).length} page{Object.keys(loadedPages).length === 1 ? '' : 's'} loaded
                  </span>
                </p>
                {distanceLookups && !withoutKm && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    Google lookups this view: {distanceLookups.google} · from saved: {distanceLookups.cached}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!showFilterForm && (
              <button
                onClick={() => setShowFilterForm(true)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                Apply Filters Again
                {activeFiltersCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">{activeFiltersCount}</span>
                )}
              </button>
            )}
            <button onClick={handleClearAll} className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
              Clear
            </button>
            <button
              onClick={fetchExportData}
              disabled={!hasSearched || exporting}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
            >
              {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
              {exporting ? 'Preparing...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {/* Filter Form */}
      {showFilterForm && (
        <FilterForm
          title={config.title}
          showSlabFilters={!withoutKm}
          filters={draftFilters}
          onChange={handleFilterChange}
          onApply={handleApplyFilters}
          onReset={handleResetDraft}
        />
      )}

      {/* Table */}
      {!showFilterForm && (
        <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full">
              {/* Sticky header: stays visible while the body scrolls vertically.
                  Background + shadow live on the th (a thead border scrolls away with sticky cells). */}
              <thead className="bg-gray-50">
                <tr>
                  <th className="sticky top-0 z-10 bg-gray-50 shadow-[inset_0_-1px_0_0_#e5e7eb] px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">#</th>
                  {TABLE_COLUMNS.map(({ key, label }) => (
                    <th key={key as string} className="sticky top-0 z-10 bg-gray-50 shadow-[inset_0_-1px_0_0_#e5e7eb] px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={TABLE_COLUMNS.length + 1} className="px-4 py-10 text-center text-sm text-gray-600">
                      <FaSpinner className="w-5 h-5 animate-spin text-blue-600 inline mr-2" />Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={TABLE_COLUMNS.length + 1} className="px-4 py-10 text-center text-sm text-gray-600">
                      No data found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => {
                    const rowNumber = (pagination.current_page - 1) * pagination.items_per_page + index + 1;
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{rowNumber}</td>
                        {TABLE_COLUMNS.map(({ key }) => (
                          <td key={key as string} className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {cellValue(key, row)}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            pagination={paginationAdapter}
            onPageChange={(nextPage) => { if (hasSearched) setPage(nextPage); }}
            onLimitChange={(nextLimit) => {
              setPagination((prev) => ({ ...prev, items_per_page: nextLimit }));
              setPage(1);
              setLoadedPages({});
              if (hasSearched) fetchRows(1, nextLimit, appliedFilters);
            }}
          />
        </div>
      )}

      {/* Empty state before first search */}
      {showFilterForm && !hasSearched && (
        <div className="bg-white border border-gray-200 rounded flex flex-col items-center justify-center py-16 text-center">
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-700">Please select Site Visit Date to view report.</p>
          <p className="text-xs text-gray-400 mt-1">Use the filters above and click Apply Filters.</p>
        </div>
      )}

      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename={config.exportFilename}
        title={config.exportTitle}
        description={`Total sites fetched: ${exportTotal.toLocaleString('en-IN')}`}
        summaryRows={[{ label: 'Total sites fetched', value: exportTotal }]}
      />
    </div>
  );
};

export default SiteDistanceReport;
